export type GlobalNavigationRefreshResult = 'applied' | 'stale' | 'failed'

/** Identifies the context region to show for a page. */
export interface GlobalNavigationContext {
  path: string
  /** A context object type (`repository`, `organization`, `team`, `user`, or `business`), or `preset`. */
  crumbType: string
  /** The context object's identifier, such as `owner/name` for a repository, or a preset name like `topics`. */
  crumbObjectIdentifier: string
  /** The active local navigation tab. Required for contexts with tabs, and omitted for contexts without them. */
  selectedLink?: string
  /**
   * Path prefix where this context stays valid, such as a repository's `/owner/name`. When history moves to an
   * entry without a stored context and outside this prefix, the page reloads so the server renders its context.
   */
  scope?: string
}

export type GlobalNavigationRefreshOptions = GlobalNavigationContext & {
  navigationCompletion?: Promise<void>
  /** The context currently displayed, cached so returning to it doesn't need a request. */
  currentContext?: GlobalNavigationContext
}

type GlobalNavigationRefreshHandler = (
  options: GlobalNavigationRefreshOptions,
) => Promise<GlobalNavigationRefreshResult>

let registeredGlobalNavigationRefreshHandler: GlobalNavigationRefreshHandler | undefined
let pendingGlobalNavigationRefresh: {refresh?: Promise<GlobalNavigationRefreshResult>} | undefined

export function registerGlobalNavigationRefreshHandler(handler: GlobalNavigationRefreshHandler): () => void {
  registeredGlobalNavigationRefreshHandler = handler
  return () => {
    if (registeredGlobalNavigationRefreshHandler === handler) {
      registeredGlobalNavigationRefreshHandler = undefined
    }
  }
}

export function refreshGlobalNavigation(
  options: GlobalNavigationRefreshOptions,
): Promise<GlobalNavigationRefreshResult> {
  const pendingRefresh: {refresh?: Promise<GlobalNavigationRefreshResult>} = {}
  pendingGlobalNavigationRefresh = pendingRefresh
  const refresh = runGlobalNavigationRefresh(options, pendingRefresh)
  pendingRefresh.refresh = refresh
  return refresh
}

async function runGlobalNavigationRefresh(
  options: GlobalNavigationRefreshOptions,
  pendingRefresh: {refresh?: Promise<GlobalNavigationRefreshResult>},
): Promise<GlobalNavigationRefreshResult> {
  try {
    return await (registeredGlobalNavigationRefreshHandler?.(options) ?? Promise.resolve('failed'))
  } finally {
    if (pendingGlobalNavigationRefresh === pendingRefresh) pendingGlobalNavigationRefresh = undefined
  }
}

/** Runs `callback` once an active refresh has applied the destination context, and skips stale or failed refreshes. */
export async function runAfterPendingGlobalNavigationRefresh(callback: () => void): Promise<void> {
  const pendingRefresh = pendingGlobalNavigationRefresh?.refresh
  if (pendingRefresh && (await pendingRefresh) !== 'applied') return
  callback()
}
